import {
  CommonFnCallMessage,
  CommonLimitedListState,
  CommonMessage,
  PlaygroundSeedMessage,
} from "../../../types";

// The message types a limited list holds; the Add menu offers exactly
// these.
export type LimitedListMessageType = "ContentMessage" | "FnCallMessage";

// FnCallMessage seeds carry the full call (name + args); a ContentMessage
// carries at most a call *reference* ({id, type}), so the name is the
// discriminant.
const isFnCallMessage = (
  message: CommonMessage
): message is CommonFnCallMessage =>
  "function_call" in message &&
  !!message.function_call &&
  typeof message.function_call === "object" &&
  "name" in message.function_call;

// A blank message for the Add menu. The source matches what re-ingest would
// derive for the type, and there is no origin provenance: the message is
// the client's own. The draft keys it under its component; the run targets
// it there.
export const blankSeedMessage = (
  messageType: LimitedListMessageType
): PlaygroundSeedMessage =>
  messageType === "ContentMessage"
    ? { message_type: "ContentMessage", source: "user", content: "" }
    : {
        message_type: "FnCallMessage",
        source: "assistant",
        function_call: { name: "", args: {} },
      };

// Present a limited-list component's seed messages as the component-state
// shape the shared Agent History visualizations render. A message's origin
// moment (when the export resolved one) becomes the item's moment link;
// messages without provenance — client-added or edited — render unlinked.
export const seedMessagesToLimitedListState = (
  componentId: string,
  messages: Array<PlaygroundSeedMessage>
): CommonLimitedListState => ({
  id: componentId,
  type: "limited_list",
  state: {
    data: messages.map((message) => {
      const item: CommonLimitedListState["state"]["data"][number] = {
        // The export carries each item's faithful source; fall back to the
        // re-ingest derivation (a function call is the agent's own prior
        // action) for messages without one, such as client-added drafts.
        source:
          message.source ?? (isFnCallMessage(message) ? "assistant" : "user"),
      };
      if (isFnCallMessage(message)) {
        item.function_call = message.function_call;
      } else if ("content" in message && typeof message.content === "string") {
        item.content = message.content;
      }
      if ("agency_tags" in message && message.agency_tags) {
        item.agency_tags = message.agency_tags;
      }
      if (message.origin_moment_id != null && message.origin_session_id) {
        item.moment_id = message.origin_moment_id;
        item.session_id = message.origin_session_id;
      }
      return item;
    }),
  },
});
